import { Controller } from "@hotwired/stimulus"

const COA_ROW_CLASSES = [
  "coa-row--default",
  "coa-row--prorated",
  "coa-row--amber",
  "coa-row--emerald",
  "coa-row--cyan",
  "coa-row--violet",
  "coa-row--rose",
  "coa-row--indigo",
]
const COA_PALETTE_SUFFIXES = ["amber", "emerald", "cyan", "violet", "rose", "indigo"]
const PRORATED_ACCOUNT_KEY = "pro-rated"

export default class extends Controller {
  static values = { new: Boolean }

  static targets = ["display", "form"]

  connect() {
    this.formSnapshot = null
    this.showDisplay()
  }

  edit(event) {
    event.preventDefault()
    this.formSnapshot = this.serializeForm()
    this.showForm()
  }

  cancel(event) {
    event.preventDefault()
    this.restoreForm(this.formSnapshot)
    this.showDisplay()
    this.dispatch("cancelled", { bubbles: true })
  }

  saveLocal(event) {
    event.preventDefault()

    const values = this.readValuesFromForm()
    if (this.newValue && this.blankNewRow(values)) {
      this.restoreForm(this.formSnapshot)
      this.showDisplay()
      this.dispatch("cancelled", { bubbles: true })
      return
    }

    if (this.newValue) this.commitNewRow()

    this.updateDisplay(values)
    this.formSnapshot = null
    this.showDisplay()
    this.dispatch("saved", { bubbles: true })
  }

  showDisplay() {
    this.toggle(this.displayTarget, false)
    this.toggle(this.formTarget, true)
  }

  showForm() {
    this.toggle(this.displayTarget, true)
    this.toggle(this.formTarget, false)
    this.focusFirstInput()
  }

  commitNewRow() {
    const list = this.element.parentElement
    if (!list) return

    const placeholder = this.element.cloneNode(true)
    const nextIndex = this.consumeNewIndex(list)

    this.rewriteNewAttributeIndexes(this.element, nextIndex)
    const placeholderIndex = Number.parseInt(list.dataset.invoiceNewIndex || "", 10)
    if (Number.isFinite(placeholderIndex)) {
      this.rewriteNewAttributeIndexes(placeholder, placeholderIndex)
    }
    this.element.classList.remove("invoice-line-item--new")
    this.newValue = false

    let manualBadge = this.element.querySelector(".invoice-row-badge")
    if (!manualBadge) {
      manualBadge = document.createElement("span")
      manualBadge.className = "invoice-row-badge"
      manualBadge.textContent = "Manual"
      const description = this.element.querySelector(".invoice-line-item__value--description")
      if (description) description.appendChild(manualBadge)
    }

    const button = this.element.querySelector(".invoice-row-edit-trigger")
    this.configureAsEditButton(button)

    this.resetPlaceholder(placeholder)
    list.appendChild(placeholder)
  }

  resetPlaceholder(placeholder) {
    placeholder.classList.add("invoice-line-item--new")
    placeholder.dataset.invoiceRowEditNewValue = "true"

    const descriptionText = placeholder.querySelector(
      ".invoice-line-item__description-text",
    )
    const descriptionDisplay = placeholder.querySelector(
      ".invoice-line-item__value--description",
    )
    if (descriptionText) descriptionText.textContent = "Add missed row"
    if (descriptionDisplay) descriptionDisplay.setAttribute("title", "Add missed row")

    const amountDisplay = placeholder.querySelector(".invoice-line-item__value--amount")
    if (amountDisplay) amountDisplay.textContent = "—"

    const accountDisplay = placeholder.querySelector(
      ".invoice-line-item__value--account",
    )
    if (accountDisplay) accountDisplay.textContent = "—"

    const noteBadge = placeholder.querySelector(".invoice-note-badge")
    if (noteBadge) noteBadge.remove()

    const manualBadge = placeholder.querySelector(".invoice-row-badge")
    if (manualBadge) manualBadge.remove()

    const button = placeholder.querySelector(".invoice-row-edit-trigger")
    this.configureAsAddButton(button)

    placeholder
      .querySelectorAll("input:not([type='hidden']), textarea, select")
      .forEach((field) => {
        if (field.type === "checkbox") {
          field.checked = false
        } else {
          field.value = ""
        }
      })
  }

  consumeNewIndex(list) {
    const current = Number.parseInt(list.dataset.invoiceNewIndex || "", 10)
    if (Number.isFinite(current)) {
      list.dataset.invoiceNewIndex = String(current + 1)
      return current
    }

    const existingIndexes = Array.from(
      list.querySelectorAll("input[name*='new_line_items_attributes'], textarea[name*='new_line_items_attributes']"),
    )
      .map((field) => field.name.match(/new_line_items_attributes\]\[(\d+)\]/))
      .filter((match) => match)
      .map((match) => Number.parseInt(match[1], 10))
      .filter((value) => Number.isFinite(value))

    const next = existingIndexes.length === 0 ? 0 : Math.max(...existingIndexes) + 1
    list.dataset.invoiceNewIndex = String(next + 1)
    return next
  }

  rewriteNewAttributeIndexes(container, index) {
    const pattern = /(\[new_line_items_attributes\]\[)\d+(\])/
    container
      .querySelectorAll("input[name], textarea[name], select[name]")
      .forEach((field) => {
        if (!field.name.includes("[new_line_items_attributes][")) return
        field.name = field.name.replace(pattern, `$1${index}$2`)
      })
  }

  readValuesFromForm() {
    return {
      description: this.fieldValue("description"),
      lineTotal: this.fieldValue("line_total"),
      accountName: this.fieldValue("account_name"),
      correctionNote: this.fieldValue("correction_note"),
    }
  }

  blankNewRow(values) {
    return (
      values.description === "" &&
      values.lineTotal === "" &&
      values.accountName === "" &&
      values.correctionNote === ""
    )
  }

  updateDisplay(values) {
    const descriptionDisplay = this.element.querySelector(
      ".invoice-line-item__value--description",
    )
    const descriptionText = this.element.querySelector(
      ".invoice-line-item__description-text",
    )
    if (descriptionDisplay && descriptionText) {
      const value = values.description || "—"
      descriptionText.textContent = value
      descriptionDisplay.setAttribute("title", value)
    } else if (descriptionDisplay) {
      const value = values.description || "—"
      descriptionDisplay.textContent = value
      descriptionDisplay.setAttribute("title", value)
    }

    const amountDisplay = this.element.querySelector(".invoice-line-item__value--amount")
    if (amountDisplay) amountDisplay.textContent = this.formatCurrency(values.lineTotal)

    const accountDisplay = this.element.querySelector(
      ".invoice-line-item__value--account",
    )
    if (accountDisplay) {
      const value = values.accountName || "—"
      accountDisplay.textContent = value
      accountDisplay.setAttribute("title", value)
      this.applyAccountRowClass(values.accountName)
    }

    this.updateNoteIndicator(values.correctionNote)
  }

  applyAccountRowClass(accountName) {
    const rowClass = this.coaRowClass(accountName)

    this.element.classList.remove(...COA_ROW_CLASSES)
    this.element.classList.add(rowClass)

    const editLineItem = this.element.querySelector(".invoice-edit-line-item")
    if (!editLineItem) return

    editLineItem.classList.remove(...COA_ROW_CLASSES)
    editLineItem.classList.add(rowClass)
  }

  formatCurrency(rawValue) {
    if (!rawValue) return "—"

    const parsed = Number.parseFloat(String(rawValue).replace(/[^0-9.-]/g, ""))
    if (!Number.isFinite(parsed)) return rawValue

    return `$${parsed.toFixed(2)}`
  }

  updateNoteIndicator(noteValue) {
    const descriptionDisplay = this.element.querySelector(
      ".invoice-line-item__value--description",
    )
    if (!descriptionDisplay) return

    const note = String(noteValue || "").trim()
    let noteBadge = descriptionDisplay.querySelector(".invoice-note-badge")

    if (!note) {
      if (noteBadge) noteBadge.remove()
      return
    }

    if (!noteBadge) {
      noteBadge = document.createElement("span")
      noteBadge.className = "invoice-note-badge"
      noteBadge.textContent = "Note"
      descriptionDisplay.appendChild(noteBadge)
    }

    noteBadge.setAttribute("title", note)
  }

  configureAsEditButton(button) {
    if (!button) return

    button.classList.remove("invoice-row-edit-trigger--always")
    button.setAttribute("aria-label", "Edit row")
    button.setAttribute("title", "Edit row")
    button.innerHTML = this.editIconMarkup()
  }

  configureAsAddButton(button) {
    if (!button) return

    button.classList.add("invoice-row-edit-trigger--always")
    button.removeAttribute("aria-label")
    button.setAttribute("title", "Add missed row")
    button.textContent = "Add"
  }

  editIconMarkup() {
    return `
      <svg class="invoice-row-edit-trigger__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">
        <path d="M12 20h9"></path>
        <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z"></path>
      </svg>
    `.trim()
  }

  fieldValue(fieldName) {
    const field = this.formTarget.querySelector(`[name$='[${fieldName}]']`)
    if (field?.type === "checkbox") return field.checked ? field.value : "0"
    return field ? field.value.trim() : ""
  }

  serializeForm() {
    const snapshot = {}
    this.formTarget
      .querySelectorAll("input[name]:not([type='hidden']), textarea[name], select[name]")
      .forEach((field) => {
        snapshot[field.name] = field.type === "checkbox" ? field.checked : field.value
      })
    return snapshot
  }

  restoreForm(snapshot) {
    if (!snapshot) return

    this.formTarget
      .querySelectorAll("input[name]:not([type='hidden']), textarea[name], select[name]")
      .forEach((field) => {
        if (Object.prototype.hasOwnProperty.call(snapshot, field.name)) {
          if (field.type === "checkbox") {
            field.checked = Boolean(snapshot[field.name])
          } else {
            field.value = snapshot[field.name]
          }
        }
      })
  }

  focusFirstInput() {
    const firstInput = this.formTarget.querySelector(
      "input:not([type='hidden']), textarea, select",
    )
    if (firstInput) firstInput.focus()
  }

  toggle(element, hidden) {
    if (!element) return

    element.classList.toggle("invoice-is-hidden", hidden)
    element.setAttribute("aria-hidden", hidden ? "true" : "false")
  }

  coaRowClass(accountName) {
    const key = String(accountName || "").trim().toLowerCase()
    if (!key) return "coa-row--default"
    if (key === PRORATED_ACCOUNT_KEY) return "coa-row--prorated"

    return `coa-row--${COA_PALETTE_SUFFIXES[this.stablePaletteIndex(key)]}`
  }

  stablePaletteIndex(key) {
    let hash = 0

    for (const char of key) {
      hash = ((hash * 31) + char.charCodeAt(0)) % COA_PALETTE_SUFFIXES.length
    }

    return hash
  }
}
